<template>
  <div>
    <!-- Os bilhetes de aviso (compra feita etc.) — sempre montados; some sozinho quando vazio. -->
    <FishAviso />
    <Loading v-if="!userId && hasJWTToken" />
    <template v-else>
      <!--
        O "pular para o conteúdo" (UI-29, D11): com uma barra de navegação e um HUD antes do
        conteúdo, o teclado levava muitas tabulações para chegar na tela. O link fica invisível até
        receber foco — ver `.pular-para-conteudo` em `base.css`.
      -->
      <a v-if="isAuthenticated" href="#conteudo" class="pular-para-conteudo">Pular para o conteúdo</a>

      <Header
        v-if="isAuthenticated"
        @open-configs="showConfigsModal = true"
        @open-paralleliums="showParalleliumsModal = true"
      />
      <div id="conteudo" class="main-content" :class="{ 'container': isAuthenticated }">
        <router-view @new-rewards="handleNewRewards"></router-view>
      </div>
      <!-- A engrenagem saiu do header e mora na folha "Mais" (D12, 3.3). -->
      <Footer v-if="isAuthenticated" @open-configs="showConfigsModal = true" />
      <BuyParalleliums v-if="showParalleliumsModal" @close="showParalleliumsModal = false" />
      <EventModal v-if="showEventModal" @close="showEventModal = false" />
      <DailyReward 
        v-if="showDailyReward"
        :show="showDailyReward"
        :reward="dailyRewardData"
        :ticket="dailyRewardticketCurrent"
        @close="handleDailyRewardClose"
      />
      <ConfigsModal 
        v-if="showConfigsModal" 
        :show="showConfigsModal" 
        @close="showConfigsModal = false"
      />
      <RewardView 
        v-if="showRewardView"
        :show="showRewardView"
        :rewards="currentRewards"
        @close="handleRewardsClose"
      />
      <TaxesModal 
        v-if="showTaxesModal" 
        @close="fecharTaxas"
      />
      <!-- O recibo da folha (0060): entra depois da taxa, nunca por cima dela. -->
      <FolhaModal
        v-if="showFolhaModal"
        :player-id="$store.state.player.id"
        :recibos="folha.recibos"
        :divida="folha.divida"
        @close="showFolhaModal = false"
      />
      <button 
        v-if="isAuthenticated" 
        @click="toggleFullscreen" 
        class="fullscreen-btn"
        :class="{ 'is-fullscreen': isFullscreen }"
      >
        <i class="fas" :class="isFullscreen ? 'fa-compress' : 'fa-expand'"></i>
      </button>
      <CookiesAcceptComponent />
      
      <!-- Language selection modal -->
      <ChoiceLanguage
        v-if="showLanguageModal"
        :show="showLanguageModal"
        @close="handleLanguageSelected"
      />
      
      <!-- Daily Missions Modal -->
      <DailyMissionsModal 
        v-if="showDailyMissionsModal" 
        @close="showDailyMissionsModal = false"
      />
      
      <!-- Daily Missions Button -->
      <button 
        v-if="isAuthenticated" 
        @click="showDailyMissionsModal = true" 
        class="daily-missions-btn"
        :class="{ 'has-complete': canClaimDailyMissionReward }"
      >
        <i class="fas fa-tasks"></i>
        <div v-if="canClaimDailyMissionReward" class="notification-dot"></div>
      </button>
      
      <CompetitionModal 
        v-if="showCompetitionModal" 
        @close="showCompetitionModal = false"
      />
      <!-- Competition Button and Modal -->
      <button 
        v-if="isAuthenticated" 
        @click="showCompetitionModal = true" 
        class="competition-btn"
        :class="{ 'has-active': hasActiveCompetitions }"
      >
        <i class="fas fa-trophy"></i>
        <div v-if="hasActiveCompetitions" class="notification-dot"></div>
      </button>
      
    </template>
  </div>
</template>

<script>
import Footer from '@/shared/footer/Footer.vue'
import Header from '@/shared/header/Header.vue'
import EventModal from '@/shared/modals/EventModal.vue'
import DailyReward from '@/shared/modals/DailyReward.vue'
import Loading from '@/shared/common/Loading.vue'
import ConfigsModal from '@/shared/modals/ConfigsModal.vue'
import RewardView from '@/shared/modals/RewardView.vue'
import CookiesAcceptComponent from '@/shared/common/CookiesAcceptComponent.vue'
import TaxesModal from '@/shared/modals/TaxesModal.vue'
import FolhaModal from '@/shared/modals/FolhaModal.vue'
import CompetitionModal from '@/shared/modals/CompetitionModal.vue'
import DailyMissionsModal from '@/shared/modals/DailyMissionsModal.vue'
import ChoiceLanguage from '@/shared/modals/ChoiceLanguage.vue'
// O parallelium subiu para o App: ele sai do HUD permanente (D6) e o botao 'Moedas' da barra e o
// atalho do HUD nas telas onde ele se gasta abrem o mesmo modal, de um lugar so.
import BuyParalleliums from '@/shared/modals/BuyParalleliums.vue'
import { FishAviso } from '@/shared/ui'
import { mapState, mapGetters } from 'vuex';
import { temSessao } from '@/core/services/api/v2/session';
import { recibosDaFolha } from '@/core/services/api/v2';

export default {
  name: 'App',
  components: {
    Footer,
    Header,
    EventModal,
    DailyReward,
    Loading,
    ConfigsModal,
    RewardView,
    CookiesAcceptComponent,
    TaxesModal,
    FolhaModal,
    CompetitionModal,
    DailyMissionsModal,
    ChoiceLanguage,
    BuyParalleliums,
    FishAviso
  },
  data() {
    return {
      showEventModal: false,
      showDailyReward: false,
      dailyRewardData: {
        type: '',
        amount: 0
      },
      dailyRewardticketCurrent: 0,
      userId: null,
      showConfigsModal: false,
      showParalleliumsModal: false,
      isFullscreen: false,
      showRewards: false,
      rewardsList: [],
      showTaxesModal: false,
      showFolhaModal: false,
      /** Os recibos da folha ainda não fechados, e a dívida de agora (0060). */
      folha: { recibos: [], divida: 0 },
      showCompetitionModal: false,
      showDailyMissionsModal: false,
      showLanguageModal: false
    }
  },
  async created() {
    // Check for language selection
    const hasSelectedLanguage = await this.$store.dispatch('language/initializeLanguage');
    this.showLanguageModal = !hasSelectedLanguage;
    
    /**
     * Tenta retomar a sessão sempre, sem olhar cookie antes.
     *
     * Antes isto era condicionado a `Cookies.get('jwt')`, o que fazia sentido
     * quando o token longo morava lá. No V2 o token longo é o refresh, num
     * cookie `httpOnly` que este código **não consegue ler** — checar antes
     * significaria mandar para o login quem tem sessão válida, toda vez que o
     * navegador fosse fechado e reaberto.
     *
     * Para quem não tem sessão nenhuma, a tentativa custa um 401 e `autoLogin`
     * devolve `false` sem lançar; a Home continua aberta a visitante.
     */
    try {
      await this.$store.dispatch('auth/autoLogin');
    } catch (error) {
      console.error('Auth check failed:', error);
      this.$router.push({ name: 'Login' });
    }
  },
  watch: {
    '$store.state.player.id': {
      immediate: true,
      handler: 'initializePlayer'
    },
    isAuthenticated(newVal) {
      if (newVal) {
        // Initialize daily missions when user is authenticated
        this.$store.dispatch('dailyMissions/initializeMissions');
      }
    }
  },
  methods: {
    /** A taxa fechou: se a folha deixou recibo, ele entra agora (0060). */
    fecharTaxas() {
      this.showTaxesModal = false;
      if (this.folha.recibos.length) this.showFolhaModal = true;
    },
    handleLanguageSelected() {
      this.showLanguageModal = false;
    },
    handleDailyRewardClose() {
      this.showDailyReward = false;
      // Show event modal after daily reward is closed
      this.showEventModal = true;
    },
    toggleFullscreen() {
      const docElm = document.documentElement;
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        if (docElm.requestFullscreen) {
          docElm.requestFullscreen().catch(err => {
            console.log(`Error attempting to enable fullscreen: ${err.message}`);
          });
        } else if (docElm.webkitRequestFullscreen) { /* Safari */
          docElm.webkitRequestFullscreen();
        }
        this.isFullscreen = true;
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) { /* Safari */
          document.webkitExitFullscreen();
        }
        this.isFullscreen = false;
      }
    },
    async checkForRewards(playerId) {
      try {
        const response = await this.$store.dispatch('rewards/getLastRewards', playerId);
        if (response && response.lastRewards && response.lastRewards.length > 0) {
          this.rewardsList = response.lastRewards;
          this.showRewards = true;
        }
      } catch (error) {
        console.error('Error checking for rewards:', error);
      }
    },
    handleNewRewards(rewards) {
      this.rewardsList = rewards;
      this.showRewards = true;
    },
    handleRewardsClose() {
      this.$store.dispatch('rewards/hideRewardView');
    },
    async initializePlayer(newId) {
      if (!newId) {
        // Saiu da sessão: o relógio não pode continuar pedindo o perfil de um
        // jogador que não está mais logado.
        this.$store.dispatch('player/pararRelogioDaEnergia');
        return;
      }
      if (newId) {
        this.userId = newId;
        /**
         * O relógio da energia entra aqui, e não numa tela.
         *
         * A regeneração é preguiçosa no servidor: ele deriva do relógio na
         * leitura e não empurra nada. Sem alguém relendo, a barra do HUD fica
         * parada — foi o defeito de 04/09/2026, com o jogo aberto a tarde
         * toda em 5/100. O HUD aparece em todas as telas, então quem relê
         * também tem que viver acima delas.
         */
        this.$store.dispatch('player/iniciarRelogioDaEnergia');
        try {
          const playerData = this.$store.state.player;

          if (this.$store.state.SoundController.isInitialized) {
            this.$store.dispatch('SoundController/playMusic', { 
              category: 'ambient',
              startPaused: false 
            });
          }

          // Check daily reward status
          if (!playerData.dailyReward) {
            try {
              const rewardResult = await this.$store.dispatch('player/claimDailyReward', playerData.id);
              if (rewardResult.success && rewardResult.reward) {
                this.dailyRewardData = {
                  type: rewardResult.reward.type,
                  amount: rewardResult.reward.amount,
                  day: rewardResult.reward.day,
                  bait: rewardResult.reward.bait
                };
                this.dailyRewardticketCurrent = rewardResult.ticket.current;
                this.$store.commit('player/SET_TICKETS', rewardResult.ticket.current);
                this.showDailyReward = true;
              }
            } catch (rewardError) {
              console.error('Error claiming daily reward:', rewardError);
            }
          }

          // Initialize daily missions
          this.$store.dispatch('dailyMissions/initializeMissions');

          // `taxes` é "imposto PENDENTE" (o `impostosPendentes` do V2, que a
          // virada liga e a cobrança desliga). No Strapi era o contrário — true
          // queria dizer "já pago" — e o `!` que ficou daquele tempo abria o
          // modal só quando não havia nada a cobrar: o servidor devolvia 409, o
          // modal fechava sozinho, e o imposto nunca foi cobrado no V2.
          if (playerData.taxes) {
            this.showTaxesModal = true;
          }

          // O recibo da folha que o worker cobrou à noite (0060). Falhar aqui não
          // pode travar a entrada: sem recibo, o jogo segue.
          try {
            const { receipts, debt } = await recibosDaFolha(playerData.id);
            this.folha = { recibos: receipts, divida: debt };
            if (receipts.length && !this.showTaxesModal) this.showFolhaModal = true;
          } catch (folhaError) {
            console.error('Error loading payroll receipts:', folhaError.message);
          }

          // Show event modal if no other modals are showing
          if (!this.showDailyReward && !this.showTaxesModal && !this.showFolhaModal) {
            this.showEventModal = true;
          }

        } catch (error) {
          console.error('Error initializing player:', error);
        }
      }
    },
    abrirQuadroDeMissoes() {
      this.showDailyMissionsModal = true;
    },
    handleMissionCompleted(event) {
      console.log('Mission completed event received:', event.detail);
      
      // Check if we detected a mission completion (either from backend or our comparison)
      const detectedCompletion = event.detail.detectedCompletion;
      
      // Check if there are any completed missions
      const hasCompletedMissions = event.detail.completedMissions > 0;
      
      // Open modal if we detected a completion or if this is a significant progress update
      if (detectedCompletion || (event.detail.updated && event.detail.progress >= 75)) {
        // Play success sound
        this.$store.dispatch('SoundController/playSound', { 
          category: 'fishing', 
          type: 'catch' 
        });
        
        // Open the daily missions modal
        this.showDailyMissionsModal = true;
      }
      
      // If we have completed missions but the modal isn't already showing,
      // add a visual indicator to the missions button
      if (hasCompletedMissions && !this.showDailyMissionsModal) {
        this.$store.dispatch('dailyMissions/fetchMissions');
      }
    }
  },
  computed: {
    ...mapState('rewards', ['showRewardView', 'currentRewards']),
    ...mapGetters('dailyMissions', ['allMissionsComplete']),
    isAuthenticated() {
      return !!this.$store.state.auth.user;
    },
    hasJWTToken() {
      return temSessao();
    },
    hasActiveCompetitions() {
      return this.$store.state.competition.competitions.some(
        comp => !comp.status.hasEnded && comp.status.hasStarted
      );
    },
    canClaimDailyMissionReward() {
      return this.allMissionsComplete;
    }
  },
  mounted() {
    // Add event listener for mission completion
    console.log('Adding mission-completed event listener');
    window.removeEventListener('mission-completed', this.handleMissionCompleted); // Remove if exists
    window.addEventListener('mission-completed', this.handleMissionCompleted);
    // A tábua de missões do Cais abre o mesmo quadro que o botão flutuante.
    window.addEventListener('abrir-missoes', this.abrirQuadroDeMissoes);

    // Add interaction listeners
    const initializeAudio = () => {
      this.$store.dispatch('SoundController/initializeAudio');
      if (this.isAuthenticated) {
        this.$store.dispatch('SoundController/playMusic', { 
          category: 'ambient',
          startPaused: false 
        });
      }
      // Remove listeners after first interaction
      document.removeEventListener('click', initializeAudio);
      document.removeEventListener('touchstart', initializeAudio);
    };

    // Add listeners for user interaction
    document.addEventListener('click', initializeAudio);
    document.addEventListener('touchstart', initializeAudio);

    // Existing fullscreen listener
    document.addEventListener('fullscreenchange', () => {
      this.isFullscreen = !!document.fullscreenElement;
    });
  },
  beforeUnmount() {
    // Remove event listener to prevent memory leaks
    console.log('Removing mission-completed event listener');
    window.removeEventListener('mission-completed', this.handleMissionCompleted);
    window.removeEventListener('abrir-missoes', this.abrirQuadroDeMissoes);
    this.$store.dispatch('player/pararRelogioDaEnergia');
  }
}
</script>

<style>
/* O reset saiu daqui para src/assets/styles/base.css (1.3, 1.4).
 *
 * As duas declarações que sumiram do seletor universal são correções, não faxina:
 *   - "position: relative" criava contexto de posicionamento em TODO nó da árvore (UI-09), o que
 *     quebra "position: absolute" contra ancestral distante e é a origem provável da escalada de
 *     "z-index" até 9999 (UI-07);
 *   - "outline: none" apagava o anel de foco do jogo inteiro (UI-29).
 *
 * A fonte também saiu: era Press Start 2P no corpo do jogo inteiro (UI-01) — a causa raiz da
 * queixa nº 1. Agora é DotGothic16 pelo token, e a Press Start 2P ficou só para a marca (D1).
 */

#app {
  font-family: var(--fonte-display);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: var(--texto);
  background: var(--fundo-pagina);
}

/**
 * O espaço que o cabeçalho e a barra ocupam.
 *
 * Os três números vinham decorados em lugares diferentes — `margin-bottom: 70px` no `body`, 80px no
 * conteúdo e 54px no telefone — e o 54px estava **errado desde o redesenho**: o cabeçalho novo tem
 * 59px no telefone, então o topo do conteúdo passava a ficar embaixo da viga.
 *
 * Agora são dois tokens, aqui, e as duas barras se medem por eles. Um cabeçalho que mude de altura
 * mexe num número só, e não em três que ninguém lembra que existem.
 */
:root {
  /* padding (8+8) + a marca (40) + a borda de latão (3) */
  --altura-cabecalho: 59px;
  /* recuo de cima (4) + a folha ATIVA, que é a maior, a 3× (99) − os 4px que ela entra por baixo da
   * tela. Mede-se pela ativa e não pelas outras (66) para o conteúdo nunca ficar escondido atrás
   * dela; a faixa com o nome ainda avança sobre o conteúdo, e isso é de propósito. Não há mais borda
   * de latão: a barra virou seis folhas presas na aresta de baixo — ver o topo do `Footer.vue`. */
  --altura-barra: calc(99px + env(safe-area-inset-bottom, 0px));
}

@media screen and (min-width: 600px) {
  :root {
    --altura-cabecalho: 67px; /* a marca cresce para 48px no tablet e no desktop */
    /* recuo de cima (4) + a folha ativa a 4× (132) − os 4px por baixo da tela. Ver a media query do
     * `Footer.vue`, que é onde os dois números têm que continuar batendo. */
    --altura-barra: calc(132px + env(safe-area-inset-bottom, 0px));
  }
}

.main-content.container {
  margin-top: var(--altura-cabecalho);
  /* SEM margin-bottom: ele abria um vão da cor do body entre a página e a barra — a tarja escura
   * que o autor apontou (03/09). A página pinta até o fim e reserva o próprio respiro; a barra
   * flutua por cima do cenário, não sobre um vazio. */
}

/**
 * Os três botões flutuantes (missões, competição, tela cheia) eram círculos azul/amarelo com
 * gradiente e PULSAVAM em loop — três violações do D0 penduradas em toda tela. Agora são placas de
 * madeira do pack, empilhadas acima da barra, e a novidade se anuncia com um aro de latão aceso —
 * nunca com pulso nem bolinha vermelha.
 */
.fullscreen-btn,
.daily-missions-btn,
.competition-btn {
  position: fixed;
  right: 16px;
  width: 48px;
  height: 48px;
  /* Abaixo do véu do modal (--z-modal: 500): um modal aberto cobre os botões flutuantes — eles
   * apareciam POR CIMA do véu, flutuando sobre a janela. */
  z-index: 400;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: var(--f-3);
  color: var(--papel-luz);

  border-style: solid;
  border-color: transparent;
  border-width: var(--placa-borda);
  border-image-source: url('/ui/botao-neutro.png');
  border-image-slice: var(--placa-fatia);
  border-image-width: var(--placa-borda);
  border-image-repeat: repeat;
  background: var(--madeira);
  background-clip: padding-box;
  image-rendering: pixelated;
  box-shadow: 0 3px 0 rgba(0, 0, 0, 0.35);

  transition: transform var(--t-rapido) var(--curva-quique), filter var(--t-rapido) var(--curva-suave);
}

.fullscreen-btn { bottom: calc(var(--altura-barra) + 16px); }
.competition-btn { bottom: calc(var(--altura-barra) + 76px); }
.daily-missions-btn { bottom: calc(var(--altura-barra) + 136px); }

@media (hover: hover) {
  .fullscreen-btn:hover,
  .daily-missions-btn:hover,
  .competition-btn:hover {
    filter: brightness(1.15);
  }
}

.fullscreen-btn:active,
.daily-missions-btn:active,
.competition-btn:active {
  transform: translateY(2px);
}

/* Novidade = aro de latão aceso, parado (D0: nada pisca; brilho suave anuncia). */
.daily-missions-btn.has-complete,
.competition-btn.has-active {
  border-image-source: url('/ui/botao-primario.png');
  background: var(--latao);
  color: var(--ouro-claro);
  box-shadow: var(--brilho-suave, 0 0 10px rgba(217, 164, 65, 0.5)), 0 3px 0 rgba(0, 0, 0, 0.35);
}

/* O ponto vermelho de notificação morreu com o pulso: o aro aceso já diz tudo. */
.notification-dot {
  display: none;
}

@media screen and (max-width: 480px) {
  .fullscreen-btn,
  .daily-missions-btn,
  .competition-btn {
    right: 8px;
    width: 44px;
    height: 44px;
  }
}
</style>
